import { ApiOutlined, CloudSyncOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Card, Flex, Form, Input, Space, Switch, Tag, Typography } from 'antd';
import { useIntl } from 'react-intl';
import type { FieldAppSettings } from '@/app/settings/app-settings';
import { FIELD_AUTO_SYNC_STATUSES, type FieldAutoSyncStatus } from '@/features/sync/engine/field-auto-sync';
import { autoSyncStatusMessages, messages } from './messages';

export function SettingsApiCard({
  autoSyncStatus,
  defaultApiBaseUrl,
  isLoading,
  isSaving,
  normalizedSettings,
  onResetApiBaseUrl,
  onSave,
  onUpdate,
  settings,
}: {
  autoSyncStatus: FieldAutoSyncStatus;
  defaultApiBaseUrl: string;
  isLoading: boolean;
  isSaving: boolean;
  normalizedSettings: FieldAppSettings;
  onResetApiBaseUrl: () => void;
  onSave: () => void;
  onUpdate: (patch: Partial<FieldAppSettings>) => void;
  settings: FieldAppSettings;
}) {
  const intl = useIntl();
  return (
    <Card loading={isLoading} size="small" title={intl.formatMessage(messages.settingsApiTitle)}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Flex gap="small" wrap>
          <Tag color={normalizedSettings.apiBaseUrl ? 'blue' : 'red'} icon={<ApiOutlined />}>
            {normalizedSettings.apiBaseUrl
              ? intl.formatMessage(messages.settingsApiConfigured)
              : intl.formatMessage(messages.settingsApiMissing)}
          </Tag>
          <Tag
            color={autoSyncStatus === FIELD_AUTO_SYNC_STATUSES.failed ? 'red' : 'green'}
            icon={<CloudSyncOutlined />}
          >
            {intl.formatMessage(messages.settingsAutoSyncStatus, {
              status: intl.formatMessage(autoSyncStatusMessages[autoSyncStatus]),
            })}
          </Tag>
        </Flex>

        <Form layout="vertical" onFinish={onSave}>
          <Form.Item
            extra={
              defaultApiBaseUrl
                ? intl.formatMessage(messages.settingsDefaultApiBaseUrl, { url: defaultApiBaseUrl })
                : intl.formatMessage(messages.settingsNoDefaultApiBaseUrl)
            }
            label={intl.formatMessage(messages.settingsApiBaseUrl)}
            required
          >
            <Input
              disabled={isSaving}
              inputMode="url"
              onChange={event => onUpdate({ apiBaseUrl: event.target.value })}
              placeholder={intl.formatMessage(messages.settingsApiBaseUrlPlaceholder)}
              prefix={<ApiOutlined />}
              size="large"
              value={settings.apiBaseUrl}
            />
          </Form.Item>

          <Form.Item label={intl.formatMessage(messages.settingsAutoSync)}>
            <Flex align="center" gap="middle" justify="space-between">
              <Typography.Text type="secondary">
                {intl.formatMessage(messages.settingsAutoSyncDescription)}
              </Typography.Text>
              <Switch
                checked={settings.autoSyncEnabled}
                disabled={isSaving}
                onChange={checked => onUpdate({ autoSyncEnabled: checked })}
              />
            </Flex>
          </Form.Item>

          <Flex gap="small" justify="space-between" wrap>
            <Button disabled={isSaving} onClick={onResetApiBaseUrl} type="default">
              {intl.formatMessage(messages.settingsUseViteDefault)}
            </Button>
            <Button htmlType="submit" icon={<SaveOutlined />} loading={isSaving} type="primary">
              {intl.formatMessage(messages.settingsSaveSettings)}
            </Button>
          </Flex>
        </Form>
      </Space>
    </Card>
  );
}
